import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { MoreHorizontal, Palette, Pencil, Plus, Tag as TagIcon, Trash2 } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Sheet, SheetContent, SheetFooter } from '@/components/atoms/sheet';
import { Skeleton } from '@/components/atoms/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { DetailPageLayout } from '@/components/layouts';
import {
  CollapsibleSectionHeader,
  DataTableDrawerHeader,
  DataTableSearchFilterField,
  DataTableSelectFilterField,
  FormAccordionCard,
} from '@/components/molecules';
import {
  DataTableFilterSection,
  SettingsDataGrid,
  type SettingsDataGridColumn,
  type SettingsDataGridSortDir,
} from '@/components/organisms';
import { Fullbleed } from '@/contexts/fullbleed';
import { useEntityTypes, type EntityType } from '@/hooks/entity-types/useEntityTypes';
import { useFilterDraft } from '@/hooks/useFilterDraft';
import { useCreateTag, useDeleteTag, useTags, useUpdateTag } from '@/hooks/tags/useTags';
import { NOTIFI_PASTEL_COLORS } from '@/lib/design/brand-colors';
import { cn } from '@/lib/utils';
import type { Tag } from '@/types/estimate';

type TagDrawerState = 'tag-new' | 'tag-edit';
type TagColumn = 'tag' | 'color' | 'createdAt' | 'updatedAt';
type ColorFilter = 'all' | 'colored' | 'uncolored';

interface TagsSearch {
  drawer?: TagDrawerState;
  tagId?: string;
  scope?: string;
}

export const Route = createFileRoute('/_dashboard/dashboard/settings/tags')({
  validateSearch: (search: Record<string, unknown>): TagsSearch => ({
    drawer: search.drawer === 'tag-new' || search.drawer === 'tag-edit' ? search.drawer : undefined,
    tagId: typeof search.tagId === 'string' ? search.tagId : undefined,
    scope: typeof search.scope === 'string' ? search.scope : undefined,
  }),
  component: TagsPage,
});

const PAGE_SIZE = 25;
const DEFAULT_SCOPE = 'estimate';
const SUPPORTED_SCOPES = new Set([
  'contact',
  'estimate',
  'job',
  'membership',
  'pricebook_item',
  'visit',
]);
const FALLBACK_ENTITY_TYPES: EntityType[] = [
  { id: 'estimate', label: 'Estimate' },
  { id: 'contact', label: 'Contact' },
  { id: 'job', label: 'Job' },
  { id: 'membership', label: 'Membership' },
  { id: 'visit', label: 'Visit' },
  { id: 'pricebook_item', label: 'Pricebook Item' },
];

const PRESET_COLORS = [...NOTIFI_PASTEL_COLORS];

const TAG_COLUMNS: SettingsDataGridColumn<TagColumn>[] = [
  { key: 'tag', label: 'Tag', sortable: true, width: 420 },
  { key: 'color', label: 'Color', sortable: true, width: 180 },
  { key: 'createdAt', label: 'Created', sortable: true, width: 180 },
  { key: 'updatedAt', label: 'Updated', sortable: true, defaultVisible: false, width: 180 },
];

const COLOR_FILTER_OPTIONS: { value: ColorFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'colored', label: 'With color' },
  { value: 'uncolored', label: 'No color' },
];

// Deferred-apply drawer filter (NOT-391): the Color select edits a local draft;
// filtering only changes on Apply. Text search stays instant (not drafted).
interface TagFilterDraft {
  search: string;
  color: ColorFilter;
}

const TAG_FILTER_DEFAULTS: TagFilterDraft = { search: '', color: 'all' };

function compareValues(a: string | number, b: string | number) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function isHexColor(value: string) {
  return /^#[0-9A-Fa-f]{6}$/.test(value.trim());
}

function normalizeColor(value: string) {
  const trimmed = value.trim();
  return isHexColor(trimmed) ? trimmed.toUpperCase() : PRESET_COLORS[0];
}

function hasColor(tag: Tag) {
  return Boolean(tag.color && isHexColor(tag.color));
}

function getSortValue(column: TagColumn, tag: Tag) {
  switch (column) {
    case 'tag':
      return tag.name.toLowerCase();
    case 'color':
      return tag.color?.toLowerCase() ?? '';
    case 'createdAt':
      return Date.parse(tag.createdAt) || 0;
    case 'updatedAt':
      return Date.parse(tag.updatedAt) || 0;
  }
}

function pluralize(label: string) {
  return label.endsWith('s') ? label : `${label}s`;
}

function colorStyles(color: string | null | undefined) {
  if (!color || !isHexColor(color)) return undefined;
  return {
    backgroundColor: `${color}20`,
    borderColor: `${color}55`,
  };
}

function normalizeEntityTypes(entityTypes: EntityType[]) {
  const source = entityTypes.length > 0 ? entityTypes : FALLBACK_ENTITY_TYPES;
  const seen = new Set<string>();
  const supported = source.filter((type) => {
    if (!SUPPORTED_SCOPES.has(type.id) || seen.has(type.id)) return false;
    seen.add(type.id);
    return true;
  });

  return supported.length > 0 ? supported : FALLBACK_ENTITY_TYPES;
}

function TagsPage() {
  const router = useRouter();
  const searchParams = Route.useSearch();
  const { data: rawEntityTypes = [], isLoading: loadingTypes } = useEntityTypes();

  const entityTypes = useMemo(() => normalizeEntityTypes(rawEntityTypes), [rawEntityTypes]);
  const activeScope = useMemo(() => {
    if (searchParams.scope && entityTypes.some((type) => type.id === searchParams.scope)) {
      return searchParams.scope;
    }
    if (entityTypes.some((type) => type.id === DEFAULT_SCOPE)) return DEFAULT_SCOPE;
    return entityTypes[0]?.id ?? DEFAULT_SCOPE;
  }, [entityTypes, searchParams.scope]);
  const activeType =
    entityTypes.find((type) => type.id === activeScope) ??
    entityTypes[0] ??
    FALLBACK_ENTITY_TYPES[0];

  const {
    data: tags = [],
    isLoading: tagsLoading,
    isError: tagsError,
    refetch,
  } = useTags(activeScope);
  const deleteTag = useDeleteTag(activeScope);

  const [search, setSearch] = useState('');
  const [colorFilter, setColorFilter] = useState<ColorFilter>('all');
  const [sortKey, setSortKey] = useState<TagColumn | null>('tag');
  const [sortDir, setSortDir] = useState<SettingsDataGridSortDir>('asc');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<Tag | null>(null);

  const editTag = useMemo(
    () => tags.find((tag) => tag.id === searchParams.tagId) ?? null,
    [searchParams.tagId, tags],
  );

  const filteredTags = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tags.filter((tag) => {
      const matchesSearch =
        !q || [tag.name, tag.color ?? '', activeType.label].join(' ').toLowerCase().includes(q);
      const matchesColor =
        colorFilter === 'all' ||
        (colorFilter === 'colored' && hasColor(tag)) ||
        (colorFilter === 'uncolored' && !hasColor(tag));
      return matchesSearch && matchesColor;
    });
  }, [activeType.label, colorFilter, search, tags]);

  const sortedTags = useMemo(() => {
    if (!sortKey) return filteredTags;
    return [...filteredTags].sort((a, b) => {
      const result = compareValues(getSortValue(sortKey, a), getSortValue(sortKey, b));
      return sortDir === 'asc' ? result : -result;
    });
  }, [filteredTags, sortDir, sortKey]);

  const activeFilterCount = colorFilter !== 'all' ? 1 : 0;
  const showClearAll = Boolean(search) || activeFilterCount > 0;
  const totalPages = Math.max(1, Math.ceil(sortedTags.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageTags = sortedTags.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const drawerMode = searchParams.drawer === 'tag-new' ? 'create' : 'edit';
  const drawerOpen = searchParams.drawer === 'tag-new' || searchParams.drawer === 'tag-edit';

  function handleScopeChange(scope: string) {
    void router.navigate({
      to: '/dashboard/settings/tags',
      search: { scope },
    });
    setSearch('');
    setColorFilter('all');
    setPage(1);
  }

  function openCreateDrawer() {
    void router.navigate({
      to: '/dashboard/settings/tags',
      search: { scope: activeScope, drawer: 'tag-new' },
    });
  }

  function openEditDrawer(tagId: string) {
    void router.navigate({
      to: '/dashboard/settings/tags',
      search: { scope: activeScope, drawer: 'tag-edit', tagId },
    });
  }

  function closeDrawer() {
    void router.navigate({
      to: '/dashboard/settings/tags',
      search: { scope: activeScope },
    });
  }

  function resetFilters() {
    setSearch('');
    setColorFilter('all');
    setPage(1);
  }

  function handleSort(column: SettingsDataGridColumn<TagColumn>, dir: SettingsDataGridSortDir) {
    if (!column.sortable) return;
    setSortKey(column.key);
    setSortDir(dir);
    setPage(1);
  }

  function handleDelete(tag: Tag) {
    deleteTag.mutate(tag.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  function renderCell(key: TagColumn, tag: Tag) {
    switch (key) {
      case 'tag':
        return <TagIdentity tag={tag} scopeLabel={activeType.label} />;
      case 'color':
        return hasColor(tag) ? (
          <span className="inline-flex items-center gap-2 font-mono text-xs text-muted-foreground">
            <span className="h-3 w-3 rounded-full border" style={{ backgroundColor: tag.color! }} />
            {tag.color}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'createdAt':
        return <span className="text-muted-foreground">{formatDate(tag.createdAt)}</span>;
      case 'updatedAt':
        return <span className="text-muted-foreground">{formatDate(tag.updatedAt)}</span>;
    }
  }

  // Drawer select writes to a draft; committed colorFilter still drives the list
  // and the activeFilterCount badge. Search stays instant (not drafted).
  const committedFilters = useMemo<TagFilterDraft>(
    () => ({ search, color: colorFilter }),
    [search, colorFilter],
  );
  const filterDraft = useFilterDraft(
    committedFilters,
    (next) => {
      setSearch(next.search);
      setColorFilter(next.color);
      setPage(1);
    },
    TAG_FILTER_DEFAULTS,
  );
  const { draft } = filterDraft;
  const draftFilterCount = (draft.search.trim() ? 1 : 0) + (draft.color !== 'all' ? 1 : 0);

  const filterContent = (
    <>
      <DataTableFilterSection
        label="Tag"
        active={!!draft.search}
        onClear={() => filterDraft.setDraft({ search: '' })}
      >
        <DataTableSearchFilterField
          value={draft.search}
          onValueChange={(value) => filterDraft.setDraft({ search: value })}
          placeholder="Search by name or color..."
        />
      </DataTableFilterSection>
      <DataTableFilterSection
        label="Color"
        active={draft.color !== 'all'}
        onClear={() => filterDraft.setDraft({ color: 'all' })}
      >
        <DataTableSelectFilterField
          value={draft.color}
          onValueChange={(value) => filterDraft.setDraft({ color: value as ColorFilter })}
          placeholder="All colors"
          options={COLOR_FILTER_OPTIONS}
        />
      </DataTableFilterSection>
    </>
  );

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <>
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold">Tags</h1>
              <p className="text-xs text-muted-foreground">
                Manage labels used to categorize records across the workspace.
              </p>
            </div>
            <Button size="sm" onClick={openCreateDrawer}>
              <Plus className="h-4 w-4" />
              Add tag
            </Button>
          </>
        }
        contentClassName="flex min-h-0 flex-col p-0"
      >
        <Tabs value={activeScope} onValueChange={handleScopeChange} className="min-h-0 flex-1">
          <SettingsDataGrid
            columns={TAG_COLUMNS}
            rows={pageTags}
            getRowKey={(tag) => tag.id}
            renderCell={(key, tag) => renderCell(key, tag)}
            storageKey={`settings-tags-${activeScope}`}
            emptyIcon={TagIcon}
            emptyTitle={tags.length === 0 ? 'No tags yet' : 'No matching tags found'}
            searchValue={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            searchPlaceholder={`Search ${pluralize(activeType.label).toLowerCase()} tags...`}
            showClearAll={showClearAll}
            onClearAll={resetFilters}
            activeFilterCount={activeFilterCount}
            toolbarActions={
              <TabsList
                variant="line"
                className="h-auto max-w-full justify-start overflow-x-auto rounded-none border-b-0 p-0"
              >
                {loadingTypes && rawEntityTypes.length === 0
                  ? FALLBACK_ENTITY_TYPES.map((type) => (
                      <Skeleton key={type.id} className="h-8 w-24 rounded-md" />
                    ))
                  : entityTypes.map((type) => (
                      <TabsTrigger key={type.id} value={type.id}>
                        <TagIcon className="size-3.5" />
                        {pluralize(type.label)}
                      </TabsTrigger>
                    ))}
              </TabsList>
            }
            filterSheet={{
              onReset: filterDraft.reset,
              resetDisabled: draftFilterCount === 0,
              onApply: filterDraft.apply,
              onOpen: filterDraft.seed,
              content: filterContent,
            }}
            showExport={false}
            isLoading={tagsLoading}
            isError={tagsError}
            onRetry={() => void refetch()}
            skeletonRowCount={8}
            onRowClick={(tag) => openEditDrawer(tag.id)}
            renderRowActions={(tag) => (
              <TagRowActions
                onEdit={() => openEditDrawer(tag.id)}
                onDelete={() => setDeleteTarget(tag)}
              />
            )}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
            pagination={{
              count: sortedTags.length,
              singular: 'tag',
              page: currentPage,
              totalPages,
              onPrevious: () => setPage((current) => Math.max(1, current - 1)),
              onNext: () => setPage((current) => Math.min(totalPages, current + 1)),
            }}
          />
        </Tabs>
      </DetailPageLayout>

      <TagFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        tag={editTag}
        scope={activeScope}
        scopeLabel={activeType.label}
        onClose={closeDrawer}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete tag?</DialogTitle>
            <DialogDescription>
              {deleteTarget ? (
                <>
                  Delete <strong>{deleteTarget.name}</strong>? This removes it from any assigned
                  {` ${pluralize(activeType.label).toLowerCase()}`}.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={deleteTag.isPending}
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteTag.isPending}
              onClick={() => {
                if (deleteTarget) handleDelete(deleteTarget);
              }}
            >
              {deleteTag.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TagIdentity({ tag, scopeLabel }: { tag: Tag; scopeLabel: string }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span
        className="h-3 w-3 shrink-0 rounded-full"
        style={{ backgroundColor: tag.color ?? 'hsl(var(--muted-foreground) / 0.4)' }}
      />
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-sm font-medium">{tag.name}</p>
        </div>
        <p className="truncate text-xs text-muted-foreground">{scopeLabel}</p>
      </div>
    </div>
  );
}

function TagPreview({ tag }: { tag: Tag }) {
  return (
    <Badge variant="secondary" className="max-w-32 gap-1.5 truncate" style={colorStyles(tag.color)}>
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: tag.color ?? 'hsl(var(--muted-foreground))' }}
      />
      <span className="truncate">{tag.name}</span>
    </Badge>
  );
}

function TagRowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={onDelete}>
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TagFormDrawer({
  open,
  mode,
  tag,
  scope,
  scopeLabel,
  onClose,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  tag: Tag | null;
  scope: string;
  scopeLabel: string;
  onClose: () => void;
}) {
  const createTag = useCreateTag(scope);
  const updateTag = useUpdateTag(tag?.id ?? '', scope);
  const isPending = createTag.isPending || updateTag.isPending;
  const isEdit = mode === 'edit';

  const [detailsOpen, setDetailsOpen] = useState(true);
  const [colorOpen, setColorOpen] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(PRESET_COLORS[0]);
  const [colorEnabled, setColorEnabled] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(tag?.name ?? '');
    setColor(normalizeColor(tag?.color ?? PRESET_COLORS[0]));
    setColorEnabled(Boolean(tag?.color));
    setDetailsOpen(true);
    setColorOpen(Boolean(tag?.color));
  }, [open, tag]);

  function handleColorChange(nextColor: string) {
    setColor(normalizeColor(nextColor));
    setColorEnabled(true);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const payload = {
      name: trimmedName,
      ...(colorEnabled ? { color: normalizeColor(color) } : {}),
    };

    if (isEdit && tag) {
      await updateTag.mutateAsync(payload);
    } else {
      await createTag.mutateAsync(payload);
    }
    onClose();
  }

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-xl"
        showCloseButton={false}
      >
        <form
          className="flex h-full min-h-0 flex-col"
          onSubmit={(event) => void handleSubmit(event)}
        >
          <DataTableDrawerHeader title={isEdit ? 'Edit Tag' : 'Create Tag'} onClose={onClose} />

          <div className="flex-1 overflow-y-auto px-5 py-5">
            <div className="space-y-5">
              <FormAccordionCard
                open={detailsOpen}
                header={
                  <CollapsibleSectionHeader
                    icon={TagIcon}
                    label="Details"
                    badge="required"
                    open={detailsOpen}
                    onToggle={() => setDetailsOpen((value) => !value)}
                  />
                }
              >
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tag-name">Name</Label>
                    <Input
                      id="tag-name"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder="e.g. Urgent, VIP, HVAC"
                      autoFocus
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Scope</Label>
                    <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                      {scopeLabel}
                    </div>
                  </div>
                </div>
              </FormAccordionCard>

              <FormAccordionCard
                open={colorOpen}
                header={
                  <CollapsibleSectionHeader
                    icon={Palette}
                    label="Color"
                    badge="optional"
                    open={colorOpen}
                    onToggle={() => setColorOpen((value) => !value)}
                    extra={<TagPreview tag={previewTag(name, colorEnabled ? color : null)} />}
                  />
                }
              >
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {PRESET_COLORS.map((presetColor) => (
                      <button
                        key={presetColor}
                        type="button"
                        onClick={() => handleColorChange(presetColor)}
                        className={cn(
                          'h-8 w-8 rounded-full border-2 transition-transform hover:scale-105',
                          colorEnabled && color === presetColor
                            ? 'border-foreground'
                            : 'border-transparent',
                        )}
                        style={{ backgroundColor: presetColor }}
                        aria-label={`Use color ${presetColor}`}
                      />
                    ))}
                    <input
                      type="color"
                      value={color}
                      onChange={(event) => handleColorChange(event.target.value)}
                      className="h-8 w-8 cursor-pointer rounded-full border-0 p-0"
                      title="Custom color"
                    />
                  </div>
                  {tag?.color ? null : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!colorEnabled}
                      onClick={() => setColorEnabled(false)}
                    >
                      Clear color
                    </Button>
                  )}
                </div>
              </FormAccordionCard>
            </div>
          </div>

          <SheetFooter className="flex-row justify-end gap-2 border-t px-5 py-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isPending}>
              {isPending ? 'Saving...' : 'Save'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function previewTag(name: string, color: string | null): Tag {
  return {
    id: 'preview',
    tenantId: 'preview',
    scope: 'preview',
    name: name.trim() || 'Preview',
    color,
    createdAt: new Date(0).toISOString(),
    updatedAt: new Date(0).toISOString(),
  };
}
